import { FC, useEffect, useRef, useState } from "react";
import "./CalendarPassSuccessScreen.css";
import { useNavigate } from "react-router-dom";
import appIcon from "../../../assets/images/header-sabehub-app-icon.svg";
import appTextIcon from "../../../assets/images/header-sabehub-text-app-icon.svg";
import allSetAnimation from "../../../assets/gif/pass-success-aniamtion.gif";
import allSetStar from "../../../assets/images/pass-success-icon.svg";
import starIcon from "../../../assets/images/membership-orange-star.svg";
import successEmoji from "../../../assets/images/payment-success-screen-success-emoji.svg";
import videoPlayIcon from "../../../assets/images/pass-success-video-play-icon.svg";
import letsDoItIcon from "../../../assets/images/lets-do-it-icon.svg";
import ROUTE_PATHS from "utils/routePaths";
import {
  formatExperiencePassTimeRange,
  formatWorkspaceTimeRange,
} from "utils/utils";
import { EXPERIENCE_TYPE_LIST, USER_TYPE } from "utils/constants";
import { useStorage } from "services/StorageProvider";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { CircularProgress } from "@mui/material";

const CalendarPassSuccessScreen: FC = () => {
  const navigate = useNavigate();
  const { userProfileData } = useStorage();
  const { sendLoginOTP } = useWSMApi();
  const progress = 40;
  const [showGif, setShowGif] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState<string | null>(null);
  const [selectedSlotData, setSelectedSlotData] = useState<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const storedSlotData = localStorage.getItem("selectedSlotData");
    if (storedSlotData) {
      setSelectedSlotData(JSON.parse(storedSlotData));
    }
  }, []);

  const sendOTPMutation = useMutation({
    mutationFn: () =>
      sendLoginOTP({ email: userProfileData?.email, type: USER_TYPE }),
    onSuccess: () => {
      navigate(ROUTE_PATHS.WSM_SIGNIN, {
        state: {
          unverified_email: userProfileData?.email,
        },
      });
    },
  });

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleLoadedMetadata = () => {
    const duration = videoRef.current?.duration;
    if (duration && isFinite(duration)) {
      const totalSeconds = Math.round(duration);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      if (mins > 0) {
        setVideoDuration(`${mins} Min${secs > 0 ? ` ${secs} Sec` : ""}`);
      } else {
        setVideoDuration(`${secs} Sec`);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowGif(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  const handleBackClick = () => {
    // const fromPath = (location.state as { from?: string } | null)?.from;
    // if (fromPath === ROUTE_PATHS.WSM_CALENDAR_PAYMENT) {
    //   navigate(-2);
    // } else {
    //   navigate(-1);
    // }
    navigate(
      ROUTE_PATHS.WSM_WORKSPACE_DETAIL.replace(":slotId", selectedSlotData?.id),
      { replace: true, state: { fromSuccessScreen: true } },
    );
  };

  const handleReserveNowClick = () => {
    if (userProfileData?.is_verified) {
      navigate(
        ROUTE_PATHS.WSM_CALENDAR_PASS_INTRODUCTION_SCREEN.replace(
          ":stage",
          "1",
        ),
      );
    } else {
      sendOTPMutation.mutate();
    }
  };

  return (
    <div className="wms-mobile-container">
      <div className="calendar-pass-success-screen-detail-screen-container">
        <div className="calendar-pass-success-screen-detail-header-container">
          <div className="left-container">
            <img src={appIcon} alt="header-app-icon" className="app-image" />
            <img src={appTextIcon} alt="header-text-app-icon" />
          </div>
          {userProfileData?.is_verified && (
            <div className="right-container" onClick={handleBackClick}>
              ✕
            </div>
          )}
        </div>
        <div className="calendar-pass-success-screen-detail-screen-content-container">
          {showGif ? (
            <img
              src={allSetAnimation}
              alt="all-set-animation"
              className="animation-container"
            />
          ) : (
            <img
              src={allSetStar}
              alt="all-set-complete"
              className="animation-container"
            />
          )}
          <div className="you-are-in-container">
            <span>You’r in</span>
            <img src={successEmoji} alt="success-emoji-icon" />
          </div>
          <div className="description-container">
            <span>
              Your Focus House{" "}
              <span style={{ color: "#FF6B35" }}>starts now</span> — not on the
              day.
            </span>
            <span>
              2 Min here, and{" "}
              <span style={{ color: "#FF6B35" }}>
                your session is built around you.
              </span>
            </span>
          </div>

          <div className="detail-container">
            <div className="text-container">
              <span style={{ color: "#C1F700" }}>Experience</span>
              <span>
                {EXPERIENCE_TYPE_LIST.find(
                  (type) => type.value === selectedSlotData?.experience_type,
                )?.name || ""}
              </span>
            </div>
            <div className="text-container">
              <span style={{ color: "#C1F700" }}>Location</span>
              <span>{selectedSlotData?.location}</span>
            </div>
            <div className="text-container">
              <span style={{ color: "#C1F700" }}>Date & time</span>
              <span>
                {formatExperiencePassTimeRange(selectedSlotData?.start_time)}
              </span>
            </div>
          </div>

          <div className="main-container">
            <span className="video-conatiner-title">
              WHY THE NEXT{" "}
              <span style={{ color: "#FF6B35" }}>2 MIn MATTER</span>
            </span>
            <span className="video-conatiner-sub-title">
              Watch this first — it unlocks what to focus on.
            </span>
            <div className="video-conatiner-divider"></div>
            <div className="video-container">
              <div className="video-player">
                {selectedSlotData?.experience_video_url ? (
                  <>
                    <video
                      ref={videoRef}
                      src={selectedSlotData?.experience_video_url}
                      controls={isPlaying}
                      playsInline
                      className="video-element"
                      onLoadedMetadata={handleLoadedMetadata}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                    >
                      Your browser does not support the video tag.
                    </video>
                    {!isPlaying && (
                      <div className="video-overlay">
                        <button
                          className="video-play-btn"
                          onClick={handlePlayClick}
                          aria-label="Play video"
                        >
                          <img
                            src={videoPlayIcon}
                            alt="play"
                            className="video-play-icon"
                          />
                        </button>
                        {videoDuration && (
                          <span className="video-duration-badge">
                            {videoDuration}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="video-placeholder">
                    <span className="video-placeholder-text">
                      Video unavailable
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="progress-container" onClick={handleReserveNowClick}>
              <span className="title">
                Set Your{" "}
                <span style={{ color: "#FF6B35" }}>Focus Intention</span>
              </span>
              <span className="description">
                We Build your Session around your One priority. Take 2 minutes
                so your Space, Meal & finish line are ready for you.
              </span>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="status-container">
                <img src={starIcon} alt="star-icon" />
                <span>Step 1. Pending</span>
              </div>
            </div>
            <span className="calendar-pass-success-screen-footer-text">
              Takes under 2 minutes
            </span>
          </div>
        </div>
      </div>
      <div
        className="calendar-pass-success-screen-detail-purchase-btn"
        onClick={handleReserveNowClick}
        // style={{ opacity: 1, pointerEvents: isLoading ? "none" : "auto" }}
      >
        {sendOTPMutation?.isPending ? (
          <CircularProgress size={30} color="inherit" />
        ) : (
          <>
            Set My Focus Goal
            <img src={letsDoItIcon} alt="lets-do-it-logo" />
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarPassSuccessScreen;
